/*
 * The editor's keymap (F-2 §8): what Enter, Shift+Enter and Ctrl+Enter do in a paragraph, a list
 * and a code block; Tab inside a list and nowhere else, so the box is never a focus trap; Backspace
 * that first takes back an input rule; the arrow that leaves a trailing code block or table; the
 * arrow-up that opens the last own message; the second break that leaves a quote; the new line in a
 * heading, which opens the paragraph under it because markdown writes a heading on one line; and
 * Ctrl+Z that reaches into the outbox when the box is empty. Escape is not here: the view claims
 * the key whether or not a command took it, so the surface answers it in its own DOM handler and
 * leaves an unused Escape to the page (EditorComposer, UX §2.3). The nine formatting chords are
 * *not* here either — they go by physical key code through `shortcutFor` in the surface's key
 * handler, and a keymap matches `key`, which is the wrong thing on a German keyboard.
 *
 * Which key sends is the person's setting *and* the pointer's: where nothing can hover, Enter breaks
 * the line whatever the setting says, and the send button is the way to post (`pointer.ts`). A list
 * is the third answer and it belongs to neither: inside one, Enter adds an item, and `Mod-Enter` is
 * the send that works from anywhere.
 */

import { chainCommands } from "prosemirror-commands"
import { redo, undo } from "prosemirror-history"
import { undoInputRule } from "prosemirror-inputrules"
import { liftListItem, sinkListItem } from "prosemirror-schema-list"
import type { Command, EditorState } from "prosemirror-state"
import { noHoverPointer } from "../composer/pointer.ts"
import { docIsBlank, type EditorKit } from "./kit.ts"

export interface KeymapDeps {
	/** Sends the message; the core refuses quietly while it cannot be sent. */
	send: () => void
	enterSends: () => boolean
	/** Ctrl+Z in an empty box: takes back the latest held message of this target. */
	outboxUndo: () => void
	/** Arrow-up in an empty box; false when the surface has no last message to open. */
	editLast: () => boolean
}

/**
 * Whether Enter adds an item where the caret stands — the question the hint under the box asks
 * (`composer.hint.list`, X-B1 A7). A code block has its own rule, and an empty item is the end of
 * the list rather than another one, so neither of them says "Enter adds an item".
 */
export function enterAddsListItem(kit: EditorKit, state: EditorState): boolean {
	const { inCodeBlock, inListItem, itemEmpty } = kit.commands
	return !inCodeBlock(state) && inListItem(state) && !itemEmpty(state)
}

/**
 * The keymap over one kit. Everything that is not about sending is the kit's: what a new line does
 * in a list, a quote, a heading or a code block is the same wherever this editor is mounted.
 */
export function buildKeymap(kit: EditorKit, deps: KeymapDeps): Record<string, Command> {
	const { N } = kit
	const {
		breakLine,
		emptyCodeBlockToParagraph,
		inCodeBlock,
		inListItem,
		insertNewline,
		itemEmpty,
		leaveTrailingBlock,
		liftEmptyItem,
		liftFirstItemAtStart,
		splitItem,
	} = kit.commands
	const send: Command = () => {
		deps.send()
		return true
	}
	// On a device whose pointer cannot hover, Enter always starts a new line and the send button is
	// what sends: a phone keyboard's return key is where a thumb lands, Shift is two taps away, and
	// the one gesture that must never be an accident is posting to a channel (D-10 D14).
	const enterSends = () => deps.enterSends() && !noHoverPointer()
	// A list is the one structure where a second line is the whole point, so Enter belongs to the
	// list there whatever the send setting says (X-B1 D2): it adds the next item, and on the item
	// nobody wrote in it leaves the list — so the Enter after that sends, which is the gesture that
	// ends a list and posts it. Ctrl+Enter still sends from inside one, and the hint under the box
	// says so while the caret is there (`enterAddsListItem`).
	const enter: Command = (state, dispatch) => {
		if (inCodeBlock(state)) return insertNewline(state, dispatch)
		if (inListItem(state)) {
			return itemEmpty(state) ? liftEmptyItem(state, dispatch) : splitItem(state, dispatch)
		}
		return enterSends() ? send(state, dispatch) : breakLine(state, dispatch)
	}
	const shiftEnter: Command = (state, dispatch) => {
		if (inCodeBlock(state)) return insertNewline(state, dispatch)
		return enterSends() ? breakLine(state, dispatch) : send(state, dispatch)
	}
	return {
		Enter: enter,
		"Shift-Enter": shiftEnter,
		"Mod-Enter": send,
		Tab: sinkListItem(N.listItem),
		"Shift-Tab": liftListItem(N.listItem),
		Backspace: chainCommands(undoInputRule, emptyCodeBlockToParagraph, liftFirstItemAtStart),
		ArrowDown: leaveTrailingBlock,
		ArrowRight: leaveTrailingBlock,
		ArrowUp: (state) => docIsBlank(state) && deps.editLast(),
		"Mod-z": (state, dispatch, view) => {
			if (!docIsBlank(state)) return undo(state, dispatch, view)
			deps.outboxUndo()
			return true
		},
		"Mod-y": redo,
		"Shift-Mod-z": redo,
	}
}
